import { useState, useRef, useCallback, useEffect } from 'react';
import { useToast } from '@/contexts/ToastContext';
import { formatINR } from '@/utils/format';

export interface BulkRow {
  id: string;
  variantName: string;
  sku: string;
  saleRate: number;
  purchaseRate: number;
  isActive: boolean;
  attributes: Record<string, string>;
}

interface ParentItem {
  id: string;
  name: string;
  code: string;
  purchaseRate?: number;
  saleRate?: number;
  unitId?: string;
  unitName?: string;
  categoryId?: string;
  categoryName?: string;
  hsnCode?: string;
  taxRate?: number;
  itemType?: string;
  minStockLevel?: number;
  isBatchTracked?: boolean;
  isSerialTracked?: boolean;
  hasExpiry?: boolean;
  shelfLifeDays?: number | null;
  requiresIncomingQC?: boolean;
  requiresFinalQC?: boolean;
  [key: string]: unknown;
}

interface ExistingVariant {
  variantName?: string | null;
  variantSku?: string | null;
  variantAttributes?: Record<string, string> | null;
  purchaseRate?: number;
  saleRate?: number;
  isActive?: boolean;
}

interface BulkVariantModalProps {
  open: boolean;
  parentItem: ParentItem | null;
  existingVariants: ExistingVariant[];
  allSkus: string[];
  onClose: () => void;
  onSave: (rows: BulkRow[], attributeNames: string[]) => void;
}

const SUGGESTIONS = [
  'Size', 'Color', 'Grade', 'Capacity',
  'Flow Rate', 'Voltage', 'Power', 'Length',
  'Diameter', 'Weight', 'Material', 'Model',
];

const FIXED_COLS: { key: keyof Omit<BulkRow, 'id' | 'attributes'>; label: string; width: string; type: string }[] = [
  { key: 'variantName', label: 'Variant Name', width: 'min-w-44 w-44', type: 'text' },
  { key: 'sku', label: 'SKU', width: 'min-w-32 w-32', type: 'text' },
  { key: 'saleRate', label: 'Sale Rate ₹', width: 'min-w-28 w-28', type: 'number' },
  { key: 'purchaseRate', label: 'Purchase Rate ₹', width: 'min-w-28 w-28', type: 'number' },
];

let rowIdCounter = 0;
function makeRow(partial: Partial<BulkRow> = {}): BulkRow {
  return {
    id: `bvr-${++rowIdCounter}`,
    variantName: '',
    sku: '',
    saleRate: 0,
    purchaseRate: 0,
    isActive: true,
    attributes: {},
    ...partial,
  };
}

function cartesianProduct(arrays: string[][]): string[][] {
  return arrays.reduce<string[][]>(
    (acc, curr) => acc.flatMap((a) => curr.map((c) => [...a, c])),
    [[]]
  );
}

export default function BulkVariantModal({ open, parentItem, existingVariants, allSkus, onClose, onSave }: BulkVariantModalProps) {
  const { success, error: toastError } = useToast();
  const [attrCols, setAttrCols] = useState<string[]>([]);
  const [attrInput, setAttrInput] = useState('');
  const [rows, setRows] = useState<BulkRow[]>([makeRow()]);
  const [focused, setFocused] = useState<{ row: number; col: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [comboModalOpen, setComboModalOpen] = useState(false);
  const [comboValues, setComboValues] = useState<Record<string, string[]>>({});
  const [comboInput, setComboInput] = useState<Record<string, string>>({});
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const inputRefs = useRef<Map<string, HTMLInputElement>>(new Map());
  const tableWrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      const savedAttrs = ((parentItem as any)?.variantAttributeNames || (parentItem as any)?.variant_attribute_names || []) as string[];
      const variantAttrKeys = new Set<string>();
      (existingVariants || []).forEach((ev) => {
        if (ev.variantAttributes) {
          Object.keys(ev.variantAttributes).forEach((k) => variantAttrKeys.add(k));
        }
      });
      const initialAttrs = Array.from(new Set([...savedAttrs, ...variantAttrKeys])).filter(Boolean);

      setAttrCols(initialAttrs);
      setAttrInput('');
      setRows([makeRow()]);
      setFocused(null);
      setSaving(false);
      setComboModalOpen(false);
      setComboValues({});
      setComboInput({});
      setCancelConfirm(false);
    }
  }, [open, parentItem, existingVariants]);

  const setRef = useCallback((key: string, el: HTMLInputElement | null) => {
    if (el) inputRefs.current.set(key, el);
    else inputRefs.current.delete(key);
  }, []);

  const cellKey = (row: number, col: string) => `${row}-${col}`;

  const focusCell = useCallback((row: number, col: string) => {
    const key = cellKey(row, col);
    const el = inputRefs.current.get(key);
    if (el) {
      el.focus();
      el.select();
      setFocused({ row, col });
      el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }, []);

  const addAttrCol = (name: string) => {
    const trimmed = name.trim();
    if (!trimmed || attrCols.includes(trimmed)) return;
    setAttrCols((prev) => [...prev, trimmed]);
    setAttrInput('');
  };

  const removeAttrCol = (name: string) => {
    setAttrCols((prev) => prev.filter((c) => c !== name));
    setRows((prev) => prev.map((r) => {
      const next = { ...r, attributes: { ...r.attributes } };
      delete next.attributes[name];
      return next;
    }));
    setComboValues((prev) => {
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const updateRow = (idx: number, field: string, value: string | number | boolean) => {
    setRows((prev) => {
      const next = [...prev];
      const row = { ...next[idx] };
      if (field.startsWith('attr:')) {
        const attrName = field.slice(5);
        row.attributes = { ...row.attributes, [attrName]: String(value) };
      } else {
        (row as Record<string, unknown>)[field] = value;
      }
      next[idx] = row;

      if (field === 'variantName' && typeof value === 'string' && value.trim() && !row.sku) {
        const parentCode = parentItem?.code || 'ITM';
        const rowNum = idx + 1;
        row.sku = `${parentCode}-${String(rowNum).padStart(2, '0')}`;
      }
      return next;
    });
  };

  const addRow = () => {
    setRows((prev) => [...prev, makeRow()]);
    setTimeout(() => focusCell(rows.length, 'variantName'), 30);
  };

  const deleteRow = (idx: number) => {
    if (rows.length <= 1) return;
    setRows((prev) => prev.filter((_, i) => i !== idx));
  };

  const allCols = [...FIXED_COLS.map((c) => c.key), ...attrCols.map((a) => `attr:${a}`), 'isActive'];

  const moveFocus = (row: number, col: string, dir: 'next' | 'prev' | 'down' | 'up') => {
    const colIdx = allCols.indexOf(col);
    if (colIdx === -1) return;

    if (dir === 'next') {
      if (colIdx < allCols.length - 1) {
        focusCell(row, allCols[colIdx + 1]);
      } else if (row < rows.length - 1) {
        focusCell(row + 1, allCols[0]);
      } else {
        setRows((prev) => [...prev, makeRow()]);
        setTimeout(() => focusCell(rows.length, allCols[0]), 40);
      }
    } else if (dir === 'prev') {
      if (colIdx > 0) focusCell(row, allCols[colIdx - 1]);
      else if (row > 0) focusCell(row - 1, allCols[allCols.length - 1]);
    } else if (dir === 'down') {
      if (row < rows.length - 1) focusCell(row + 1, col);
      else {
        setRows((prev) => [...prev, makeRow()]);
        setTimeout(() => focusCell(rows.length, col), 40);
      }
    } else if (dir === 'up') {
      if (row > 0) focusCell(row - 1, col);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, row: number, col: string) => {
    if (e.key === 'Tab' && !e.shiftKey) {
      e.preventDefault();
      moveFocus(row, col, 'next');
    } else if (e.key === 'Tab' && e.shiftKey) {
      e.preventDefault();
      moveFocus(row, col, 'prev');
    } else if (e.key === 'Enter') {
      e.preventDefault();
      moveFocus(row, col, 'next');
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      moveFocus(row, col, 'down');
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      moveFocus(row, col, 'up');
    }
  };

  const openComboModal = () => {
    if (attrCols.length === 0) {
      toastError('Define at least one attribute column before generating combinations');
      return;
    }
    const initial: Record<string, string[]> = {};
    attrCols.forEach((c) => { initial[c] = []; });
    setComboValues(initial);
    setComboInput({});
    setComboModalOpen(true);
  };

  const addComboValue = (attr: string, val: string) => {
    const trimmed = val.trim();
    if (!trimmed) return;
    setComboValues((prev) => {
      const existing = prev[attr] || [];
      if (existing.includes(trimmed)) return prev;
      return { ...prev, [attr]: [...existing, trimmed] };
    });
    setComboInput((prev) => ({ ...prev, [attr]: '' }));
  };

  const removeComboValue = (attr: string, val: string) => {
    setComboValues((prev) => ({ ...prev, [attr]: (prev[attr] || []).filter((v) => v !== val) }));
  };

  const generateCombinations = () => {
    if (!parentItem) return;
    const arrays = attrCols.map((c) => comboValues[c] || []).filter((a) => a.length > 0);
    if (arrays.length === 0 || arrays.length !== attrCols.length) {
      toastError('Add at least one value for every attribute');
      return;
    }
    const combos = cartesianProduct(arrays);
    const allSkusSet = new Set(allSkus);
    const existingTableSkus = new Set(rows.map((r) => r.sku).filter(Boolean));

    const newRows: BulkRow[] = combos.map((combo, idx) => {
      const attrs: Record<string, string> = {};
      attrCols.forEach((col, i) => { attrs[col] = combo[i]; });
      const variantName = `${parentItem.name} (${combo.join(', ')})`;

      let sku = `${parentItem.code}-${String(rows.length + idx + 1).padStart(2, '0')}`;
      let suffix = 1;
      while (allSkusSet.has(sku) || existingTableSkus.has(sku)) {
        sku = `${parentItem.code}-${String(rows.length + idx + 1).padStart(2, '0')}-${suffix++}`;
      }
      existingTableSkus.add(sku);

      return makeRow({
        variantName,
        sku,
        saleRate: parentItem.saleRate || 0,
        purchaseRate: parentItem.purchaseRate || 0,
        isActive: true,
        attributes: attrs,
      });
    });

    setRows((prev) => [...prev, ...newRows]);
    setComboModalOpen(false);
    setTimeout(() => {
      success(`${newRows.length} combination${newRows.length > 1 ? 's' : ''} generated`);
    }, 100);
  };

  const copyExistingVariants = () => {
    if (!parentItem || existingVariants.length === 0) return;
    const allSkusSet = new Set(allSkus);
    const existingTableSkus = new Set(rows.map((r) => r.sku).filter(Boolean));

    const copied: BulkRow[] = existingVariants.map((ev, idx) => {
      let sku = `${parentItem.code}-C${String(idx + 1).padStart(2, '0')}`;
      let suffix = 1;
      while (allSkusSet.has(sku) || existingTableSkus.has(sku)) {
        sku = `${parentItem.code}-C${String(idx + 1).padStart(2, '0')}-${suffix++}`;
      }
      existingTableSkus.add(sku);

      return makeRow({
        variantName: ev.variantName ? `${ev.variantName} (Copy)` : '',
        sku,
        saleRate: ev.saleRate ?? parentItem.saleRate ?? 0,
        purchaseRate: ev.purchaseRate ?? parentItem.purchaseRate ?? 0,
        isActive: ev.isActive ?? true,
        attributes: { ...(ev.variantAttributes || {}) },
      });
    });

    setRows((prev) => [...prev, ...copied]);
    setTimeout(() => {
      success(`${copied.length} existing variant${copied.length > 1 ? 's' : ''} copied as template`);
    }, 100);
  };

  const handleCancel = () => {
    const hasData = rows.some((r) =>
      r.variantName.trim() ||
      r.sku.trim() ||
      r.saleRate > 0 ||
      r.purchaseRate > 0 ||
      Object.values(r.attributes).some((v) => v.trim())
    );
    if (hasData && !cancelConfirm) {
      setCancelConfirm(true);
      return;
    }
    setCancelConfirm(false);
    onClose();
  };

  const handleSave = () => {
    if (!parentItem) return;
    if (attrCols.length === 0) {
      toastError('Define at least one attribute column before saving');
      return;
    }
    const validRows = rows.filter((r) => r.variantName.trim());
    if (validRows.length === 0) {
      toastError('Enter at least one variant name');
      return;
    }

    const names = validRows.map((r) => r.variantName.trim().toLowerCase());
    const dupes = names.filter((n, i) => names.indexOf(n) !== i);
    if (dupes.length > 0) {
      toastError(`Duplicate variant names found: ${[...new Set(dupes)].join(', ')}`);
      return;
    }

    const allSkusSet = new Set(allSkus.map((s) => s.toLowerCase()));
    const tableSkus = new Set<string>();
    for (const row of validRows) {
      const sku = row.sku.trim();
      if (!sku) {
        toastError(`SKU is required for "${row.variantName}"`);
        return;
      }
      const skuLower = sku.toLowerCase();
      if (allSkusSet.has(skuLower)) {
        toastError(`SKU "${sku}" already exists in the system`);
        return;
      }
      if (tableSkus.has(skuLower)) {
        toastError(`Duplicate SKU "${sku}" within this batch`);
        return;
      }
      tableSkus.add(skuLower);
    }

    setSaving(true);
    setTimeout(() => {
      onSave(validRows, attrCols);
      setSaving(false);
      onClose();
    }, 200);
  };

  const parentCode = parentItem?.code || 'ITM';

  const renderCell = (row: BulkRow, idx: number, col: (typeof FIXED_COLS)[number]) => {
    const key = cellKey(idx, col.key);
    const val = (row as Record<string, unknown>)[col.key];
    return (
      <input
        key={key}
        ref={(el) => setRef(key, el)}
        type={col.type}
        value={col.type === 'number' ? (val as number || '') : (val as string)}
        onChange={(e) => updateRow(idx, col.key, col.type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value)}
        onKeyDown={(e) => handleKeyDown(e, idx, col.key)}
        onFocus={() => setFocused({ row: idx, col: col.key })}
        className="w-full h-8 px-2 text-sm border border-[#e2e8f0] rounded-md bg-white focus:outline-none focus:border-[#4f46e5] focus:ring-1 focus:ring-[#4f46e5]/20"
        placeholder={col.key === 'variantName' ? 'e.g. Pump X-100' : col.key === 'sku' ? `${parentCode}-01` : ''}
      />
    );
  };

  if (!open || !parentItem) return null;

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={handleCancel} />
      <div className="relative ml-auto h-full w-full max-w-5xl bg-white shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#e2e8f0] shrink-0">
          <div>
            <h3 className="text-base font-semibold text-[#1e293b]">Add Multiple Variants</h3>
            <p className="text-xs text-[#64748b] mt-0.5">{parentItem.name} · {parentItem.code}</p>
          </div>
          <button onClick={handleCancel} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#f1f5f9] text-[#64748b] cursor-pointer">
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          {/* SECTION 1 — Attribute Columns */}
          <div>
            <div className="mb-2">
              <h4 className="text-sm font-semibold text-[#1e293b]">Step 1 — Define variant attributes</h4>
              <p className="text-xs text-[#94a3b8] mt-0.5">What makes each variant different? e.g. Flow Rate, Motor Type, Color, Size</p>
            </div>

            <div className="flex items-center gap-2 mb-3">
              <input
                type="text" value={attrInput}
                onChange={(e) => setAttrInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addAttrCol(attrInput); } }}
                placeholder="Attribute name..."
                className="h-9 px-3 rounded-lg border border-[#e2e8f0] text-sm text-[#1e293b] bg-white focus:outline-none focus:border-[#4f46e5] focus:ring-2 focus:ring-[#4f46e5]/20 w-56"
              />
              <button
                onClick={() => addAttrCol(attrInput)}
                className="h-9 px-3 rounded-lg bg-[#4f46e5] text-white text-xs font-semibold hover:bg-indigo-700 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"
              >
                <i className="ri-add-line" /> Add Column
              </button>
            </div>

            {attrCols.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {attrCols.map((col) => (
                  <span key={col} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-700 text-xs font-medium border border-indigo-100">
                    {col}
                    <button onClick={() => removeAttrCol(col)} className="w-4 h-4 flex items-center justify-center rounded-full hover:bg-indigo-200 text-indigo-500 cursor-pointer">
                      <i className="ri-close-line text-[10px]" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            <div className="flex flex-wrap gap-1.5">
              <span className="text-xs text-[#94a3b8] mr-1 self-center">Suggestions:</span>
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  onClick={() => addAttrCol(s)}
                  className={`px-2 py-0.5 rounded-md text-xs border transition-colors cursor-pointer whitespace-nowrap ${attrCols.includes(s) ? 'bg-indigo-50 border-indigo-200 text-indigo-600' : 'bg-white border-[#e2e8f0] text-[#64748b] hover:border-[#4f46e5] hover:text-[#4f46e5]'}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* SECTION 2 — Variant Table */}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <div>
                <h4 className="text-sm font-semibold text-[#1e293b]">Step 2 — Fill variant details</h4>
                <p className="text-xs text-[#94a3b8] mt-0.5">Each row = one variant. Add as many as needed.</p>
              </div>
              <span className="text-xs text-[#94a3b8]">{rows.filter((r) => r.variantName.trim()).length} valid / {rows.length} rows</span>
            </div>

            <div ref={tableWrapRef} className="border border-[#e2e8f0] rounded-xl overflow-x-auto">
              <table className="w-full text-sm border-collapse min-w-[700px]">
                <thead>
                  <tr className="bg-[#f8fafc] border-b border-[#e2e8f0]">
                    {FIXED_COLS.map((c) => (
                      <th key={c.key} className="text-left px-3 py-2 text-xs font-semibold text-[#64748b] uppercase tracking-wide whitespace-nowrap">{c.label}</th>
                    ))}
                    {attrCols.map((a) => (
                      <th key={a} className="text-left px-3 py-2 text-xs font-semibold text-[#64748b] uppercase tracking-wide whitespace-nowrap">{a}</th>
                    ))}
                    <th className="text-left px-3 py-2 text-xs font-semibold text-[#64748b] uppercase tracking-wide whitespace-nowrap w-16">Active</th>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-[#64748b] uppercase tracking-wide whitespace-nowrap w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, idx) => (
                    <tr key={row.id} className={`border-b border-[#f1f5f9] ${focused?.row === idx ? 'bg-indigo-50/40' : 'hover:bg-[#f8fafc]'}`}>
                      {FIXED_COLS.map((col) => (
                        <td key={col.key} className={`px-2 py-1.5 ${col.width}`}>
                          {renderCell(row, idx, col)}
                        </td>
                      ))}
                      {attrCols.map((a) => {
                        const k = cellKey(idx, `attr:${a}`);
                        return (
                          <td key={a} className="px-2 py-1.5 min-w-32 w-32">
                            <input
                              ref={(el) => setRef(k, el)}
                              type="text"
                              value={row.attributes[a] || ''}
                              onChange={(e) => updateRow(idx, `attr:${a}`, e.target.value)}
                              onKeyDown={(e) => handleKeyDown(e, idx, `attr:${a}`)}
                              onFocus={() => setFocused({ row: idx, col: `attr:${a}` })}
                              placeholder={`e.g. 100 LPM`}
                              className="w-full h-8 px-2 text-sm border border-[#e2e8f0] rounded-md bg-white focus:outline-none focus:border-[#4f46e5] focus:ring-1 focus:ring-[#4f46e5]/20"
                            />
                          </td>
                        );
                      })}
                      <td className="px-2 py-1.5 w-16 text-center">
                        <button
                          type="button"
                          onClick={() => updateRow(idx, 'isActive', !row.isActive)}
                          className={`relative inline-flex items-center h-5 w-10 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/40 ${
                            row.isActive ? 'bg-[#4f46e5]' : 'bg-[#cbd5e1]'
                          }`}
                          title={row.isActive ? 'Active' : 'Inactive'}
                        >
                          <span
                            className={`pointer-events-none inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out ${
                              row.isActive ? 'translate-x-5' : 'translate-x-0.5'
                            }`}
                          />
                        </button>
                      </td>
                      <td className="px-2 py-1.5 w-10">
                        <button
                          onClick={() => deleteRow(idx)}
                          disabled={rows.length <= 1}
                          className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 text-[#94a3b8] hover:text-red-500 transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <i className="ri-delete-bin-line text-sm" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <button
              onClick={addRow}
              className="mt-3 h-8 px-3 rounded-lg border border-[#e2e8f0] text-xs font-medium text-[#64748b] hover:border-[#4f46e5] hover:text-[#4f46e5] transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
            >
              <i className="ri-add-line" /> Add Row
            </button>

            <div className="mt-3 flex items-center gap-4 text-xs text-[#94a3b8]">
              <span><i className="ri-information-line mr-1" />Sale & Purchase rates default to parent: {formatINR(parentItem.saleRate ?? 0)} / {formatINR(parentItem.purchaseRate ?? 0)}</span>
              <span>·</span>
              <span>SKU auto-format: {parentCode}-01, {parentCode}-02…</span>
            </div>
          </div>

          {/* SECTION 3 — Quick Fill Tools */}
          <div>
            <div className="mb-2">
              <h4 className="text-sm font-semibold text-[#1e293b]">Step 3 — Quick fill (optional)</h4>
              <p className="text-xs text-[#94a3b8] mt-0.5">Save time by auto-generating variants</p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={openComboModal}
                disabled={attrCols.length === 0}
                className="h-8 px-3 rounded-lg border border-[#4f46e5] text-[#4f46e5] text-xs font-medium hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
              >
                <i className="ri-magic-line" /> Generate Combinations
              </button>

              {existingVariants.length > 0 && (
                <button
                  onClick={copyExistingVariants}
                  className="h-8 px-3 rounded-lg border border-[#e2e8f0] text-[#64748b] text-xs font-medium hover:border-[#4f46e5] hover:text-[#4f46e5] transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
                >
                  <i className="ri-file-copy-line" /> Copy Existing as Template
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-[#e2e8f0] shrink-0">
          <div className="text-xs text-[#94a3b8]">
            Tab/Enter to navigate · Arrow keys move between rows
          </div>
          <div className="flex items-center gap-2">
            {cancelConfirm ? (
              <div className="flex items-center gap-2">
                <span className="text-xs text-amber-600 font-medium">Discard changes?</span>
                <button
                  onClick={() => { setCancelConfirm(false); onClose(); }}
                  className="h-8 px-3 rounded-lg bg-red-50 text-red-600 text-xs font-medium hover:bg-red-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Yes, Discard
                </button>
                <button
                  onClick={() => setCancelConfirm(false)}
                  className="h-8 px-3 rounded-lg border border-[#e2e8f0] text-xs font-medium text-[#64748b] hover:bg-[#f8fafc] transition-colors cursor-pointer whitespace-nowrap"
                >
                  Keep Editing
                </button>
              </div>
            ) : (
              <>
                <button onClick={handleCancel}
                  className="h-9 px-4 rounded-lg border border-[#e2e8f0] text-sm font-medium text-[#64748b] hover:bg-[#f8fafc] transition-colors cursor-pointer whitespace-nowrap">
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving || attrCols.length === 0}
                  className="flex items-center gap-2 h-9 px-4 rounded-lg bg-[#4f46e5] text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {saving ? <><i className="ri-loader-4-line animate-spin" /> Saving…</> : <><i className="ri-save-line" /> Save All Variants</>}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Combo Generator Mini Modal */}
      {comboModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setComboModalOpen(false)} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 flex flex-col max-h-[80vh]">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#e2e8f0]">
              <div>
                <h4 className="text-sm font-semibold text-[#1e293b]">Generate Combinations</h4>
                <p className="text-xs text-[#94a3b8] mt-0.5">Define values for each attribute</p>
              </div>
              <button onClick={() => setComboModalOpen(false)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-[#f1f5f9] text-[#64748b] cursor-pointer">
                <i className="ri-close-line" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              {attrCols.map((attr) => (
                <div key={attr}>
                  <label className="block text-xs font-semibold text-[#64748b] mb-1.5">{attr}</label>
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      type="text"
                      value={comboInput[attr] || ''}
                      onChange={(e) => setComboInput((prev) => ({ ...prev, [attr]: e.target.value }))}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addComboValue(attr, comboInput[attr] || '');
                        }
                      }}
                      placeholder={`e.g. 100 LPM`}
                      className="flex-1 h-8 px-2.5 text-sm border border-[#e2e8f0] rounded-lg bg-white focus:outline-none focus:border-[#4f46e5] focus:ring-1 focus:ring-[#4f46e5]/20"
                    />
                    <button
                      onClick={() => addComboValue(attr, comboInput[attr] || '')}
                      className="h-8 px-2.5 rounded-lg bg-[#4f46e5] text-white text-xs font-medium hover:bg-indigo-700 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-add-line" />
                    </button>
                  </div>
                  {(comboValues[attr] || []).length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {(comboValues[attr] || []).map((val) => (
                        <span key={val} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-700 text-xs font-medium border border-indigo-100">
                          {val}
                          <button onClick={() => removeComboValue(attr, val)} className="w-3.5 h-3.5 flex items-center justify-center rounded hover:bg-indigo-200 text-indigo-500 cursor-pointer">
                            <i className="ri-close-line text-[9px]" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              {attrCols.every((c) => (comboValues[c] || []).length > 0) && (
                <div className="bg-[#f8fafc] rounded-lg p-3 border border-[#e2e8f0]">
                  <p className="text-xs text-[#64748b]">
                    <i className="ri-information-line mr-1" />
                    Will generate <strong className="text-[#1e293b]">
                      {attrCols.reduce((acc, c) => acc * (comboValues[c] || []).length, 1)}
                    </strong> combination{attrCols.reduce((acc, c) => acc * (comboValues[c] || []).length, 1) > 1 ? 's' : ''}
                  </p>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-[#e2e8f0]">
              <button
                onClick={() => setComboModalOpen(false)}
                className="h-8 px-3 rounded-lg border border-[#e2e8f0] text-xs font-medium text-[#64748b] hover:bg-[#f8fafc] transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={generateCombinations}
                disabled={!attrCols.every((c) => (comboValues[c] || []).length > 0)}
                className="h-8 px-3 rounded-lg bg-[#4f46e5] text-white text-xs font-semibold hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
              >
                <i className="ri-magic-line" /> Generate
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
